define((require) => {
    "use strict";

    const _                        = require("underscore");
    const $                        = require("jquery");
    const ButtonWidgetModel        = require("shared/ui/experience/widgets/button/ButtonWidgetModel");
    const ButtonType               = require("shared/ui/experience/widgets/button/ButtonType");
    const UrlUtil                  = require("shared/util/UrlUtil");
    const FontDescriptorCollection = require("shared/data/fonts/FontDescriptorCollection");
    const FontProvider             = require("shared/data/fonts/FontProvider");
    const GoogleFonts              = require("shared/services/fonts/GoogleFonts");
    const {Logger}                 = require("shared/util/Logger");
    const Preconditions            = require("shared/util/Preconditions");
    const StandardWebFontList      = require("shared/ui/assets/StandardWebFontList");
    const TextWidgetModel          = require("shared/ui/experience/widgets/textWidget/TextWidgetModel");
    const EnhancedTextWidgetModel  = require("shared/ui/experience/widgets/textEnhancedWidget/TextWidgetModel");
    const WebFontVariants          = require("shared/ui/experience/fonts/WebFontVariants").default;

    const fontStyles = ["normal", "italic", "oblique", "initial", "inherit"];

    const logger = new Logger("WebFontsUtil");

    /**
     * Convenience method for setting the `kitID` when dealing with Typekit,
     * the project Id for Fonts.com fonts and `url`, `customFontId`, `format` for custom fonts.
     *
     * @param {String} fontProviderValue The font provider string representation.
     * @param {Object} meta The current text widget metadata.
     *
     * @return {Object} `FontDescriptor` metadata for the specified font provider.
     *
     * Standard and Google fonts do not need any special treatment.
     */
    const fontDescriptorData = (fontProviderValue, meta) => {
        const fontProvider = FontProvider.fromValue(fontProviderValue);
        switch (fontProvider) {
            case FontProvider.CUSTOM:
                return {
                    path: _.result(meta, "path"),
                    customFontId: _.result(meta, "customFontId"),
                    format: _.result(meta, "format")
                };

            case FontProvider.TYPEKIT:
                return {
                    kitID: _.result(meta, "kitID")
                };

            case FontProvider.FONTS_DOT_COM:
                return {
                    projectId: _.result(meta, "projectId")
                };

            default: // No descriptors for Google and Standard fonts.
                return null;
        }
    };

    /**
     * All static utility class for working with web fonts.
     *
     * @class WebFontsUtil
     * @author Kim
     */
    const WebFontsUtil = {

        /**
         * Convenience method for ensuring backwards compatibility (pre MOS-1405). If a font provider is already
         * defined, that provider is returned. Otherwise it's safe to assume that it's either a standard font or a
         * Google font.
         *
         * @param  {String} fontFamily          The font family.
         * @param  {FontProvider} fontProvider  Optional `FontProvider`.
         * @return {FontProvider}               The correct font provider.
         */
        fontProvider(fontFamily, fontProvider) {
            if (fontProvider) {
                return fontProvider;
            }

            return WebFontsUtil.isStandardWebFont(fontFamily) ? FontProvider.STANDARD : FontProvider.GOOGLE;
        },

        /**
         * Checks if we have legacy font properties. if there is, we converts them to the new properties
         * This is introduced for the task "advance text editor"
         */
        fontCompatibilityCheck(widget) {
            const attrs = {};
            const fontFamilies    = widget.get("fontFamilies");
            const fontFamily      = widget.get("fontFamily");
            let fontStyle         = widget.get("fontStyle");
            let fontWeight        = widget.get("fontWeight");
            let fontMetadata      = widget.get("fontMetadata");
            let fontProviderValue = widget.get("fontProvider");

            // In the older experiences, we may not have a font provider
            if (!fontProviderValue) {
                const fontProvider = WebFontsUtil.fontProvider(fontFamily);
                fontProviderValue = attrs.fontProvider = fontProvider.value;
            }

            // In the old text widget, fontStyle is a font variant which is composed of
            // css font style and font weight
            if (!widget.has("fontWeight") || _.indexOf(fontStyles, fontStyle) === -1) {
                const fontVariant = widget.get("fontStyle");
                const font = WebFontsUtil.toCss(fontFamily, fontVariant, fontProviderValue);

                fontStyle = attrs.fontStyle = font.fontStyle;
                fontWeight = attrs.fontWeight = font.fontWeight;
            }

            if (!fontMetadata) {
                fontMetadata = attrs.fontMetadata = {
                    cssFamilyNames: fontFamily
                };
            } else if (!fontMetadata.cssFamilyNames) {
                fontMetadata.cssFamilyNames = fontFamily;
            }

            if (!fontFamilies || !fontFamilies.length) {
                attrs.fontFamilies = [{
                    fontProvider: fontProviderValue,
                    fontFamily,
                    fontStyle,
                    fontWeight,
                    fontMetadata
                }];
            }

            // Enhanced text widget have no attrs at this step
            // because it uses not all of old-style text widget props
            // so, construct correct attrs from fontFamilies
            // metadata and available data props
            if (!attrs.fontFamilies && fontFamilies) {
                const constructedFontFamilies = [];
                _.each(fontFamilies, (font) => {
                    font.fontStyle = fontStyle;
                    font.fontWeight = fontWeight;
                    constructedFontFamilies.push(font);
                });

                attrs.fontFamilies = constructedFontFamilies;
            }

            widget.set(attrs,  {silent: true});
        },

        /**
         * Creates a new `FontDescriptorCollection` from the specified scene or array of scenes - based on
         * any contained `TextWidgetModel`'s.
         *
         * @param  {Scene|Scene[]} scene The scene or array of scenes to load.
         * @return {FontDescriptorCollection}       The `FontDescriptorCollection`.
         */
        createFontDescriptorCollection(scene) {
            // Normalize to array
            const scenes = _.isArray(scene) ? scene : [scene];
            // Compile a list of TextWidgetModels and classic buttons (label might have a custom font)
            const widgets = _.flatten(scenes.map((scene) => {
                const textWidgets = scene.widgetsByClass(TextWidgetModel);
                const enhancedTextWidgets = scene.widgetsByClass(EnhancedTextWidgetModel);
                const buttonWidgets = scene.widgetsByClass(ButtonWidgetModel)
                    .filter((buttonWidgetModel) => (
                        buttonWidgetModel.get("buttonType") !== ButtonType.ICON_ONLY
                    ));

                return textWidgets.concat(buttonWidgets).concat(enhancedTextWidgets);
            }));

            // Construct the font descriptor collection
            return _.reduce(widgets, (collectionMemo, widget) => {
                this.fontCompatibilityCheck(widget);
                _.each(widget.get("fontFamilies"), (font) => {
                    const fontFamily = font.fontFamily;
                    const fontProviderValue = font.fontProvider;
                    const fontProvider = FontProvider.fromValue(fontProviderValue);
                    const fontVariant  = WebFontVariants.findVariant(fontProvider, font.fontStyle, font.fontWeight);

                    if (fontFamily && fontVariant) {
                        collectionMemo.addDescriptor(
                            fontFamily,
                            fontVariant.value,
                            WebFontsUtil.fontProvider(fontFamily, fontProvider),
                            fontDescriptorData(fontProviderValue, font.fontMetadata)
                        );
                    } else {
                        logger.warn(`Invalid font configuration for font family '${fontFamily}'`, {
                            provider: fontProvider,
                            font
                        });
                    }
                });

                return collectionMemo;
            }, new FontDescriptorCollection());
        },

        isStandardWebFont(fontFamily) {
            const normalizedFontFamily = fontFamily && fontFamily.toLowerCase();

            const found = normalizedFontFamily && _.find(StandardWebFontList.items, (item) => {
                if (item.family.toLowerCase() === normalizedFontFamily) {
                    return true;
                }
                // It might be an alias
                const aliases = _.invoke(item.aliases, "toLowerCase");
                return _.contains(aliases, normalizedFontFamily);
            });

            return found !== undefined;
        },

        getFontAttributes(textWidgetModel) {
            const attributes = {
                "data-font-family": textWidgetModel.attributes.fontFamily,
                "data-font-provider": textWidgetModel.attributes.fontProvider
            };

            const {
                fontMetadata: {
                    kitID,
                    projectId,
                    path,
                    customFontId,
                    format
                }
            } = textWidgetModel.attributes;

            if (kitID) {
                attributes["data-kitid"] = kitID;
            }

            if (projectId) {
                attributes["data-projectid"] = projectId;
            }

            if (path && customFontId && format) {
                attributes["data-path"] = path;
                attributes["data-customfontid"] = customFontId;
                attributes["data-format"] = format;
            }

            return attributes;
        },

        getCssFontFamily(textWidgetModel) {
            const fontFamily = textWidgetModel.get("fontFamily");
            if (fontFamily) {
                const provider = WebFontsUtil.fontProvider(fontFamily, FontProvider.fromValue(textWidgetModel.get("fontProvider")));

                switch (provider) {
                    case FontProvider.STANDARD:
                        return StandardWebFontList.cssFamilyNames(fontFamily);

                    case FontProvider.GOOGLE:
                    case FontProvider.CUSTOM:
                        return fontFamily;

                    case FontProvider.TYPEKIT:
                    case FontProvider.FONTS_DOT_COM:
                        return textWidgetModel.get("fontMetadata")
                            .cssFamilyNames
                            .toString();
                    default: // all providers covered above, cannot recognize this one.
                        // TODO Candidate for refactoring during MOS-2934.
                        Preconditions.warn(`DefaultSwitchWarn - Provider '${provider}' not recognized.`);
                        break;
                }
            }
        },

        /**
         * Returns the needed CSS object for rendering the specified `TextWidgetModel`.
         *
         * @param  {TextWidgetModel} textWidgetModel    The `TextWidgetModel`.
         * @return {Object}                             An object representing the font related CSS, e.g.:
         *                                              <pre>
         *                                              {
         *                                                  fontWeight: "700",
         *                                                  fontStyle: "normal",
         *                                                  fontFamily: "Arial"
         *                                              }
         *                                              </pre>
         */
        fontCssFor(textWidgetModel) {
            const fontFamily = textWidgetModel.get("fontFamily");
            const provider = WebFontsUtil.fontProvider(fontFamily, FontProvider.fromValue(textWidgetModel.get("fontProvider")));

            // Fall back on a default variant to be compatible with manually entered (non existing) legacy
            // font styles (pre custom fonts)
            const variant = WebFontVariants.fromValue(textWidgetModel.get("fontStyle"), provider) || WebFontVariants.defaultVariant(provider);

            return {
                fontWeight: variant.weight,
                fontStyle: variant.style,
                fontFamily: this.getCssFontFamily(textWidgetModel)
            };
        },

        toCss(fontFamily, fontVariant, fontProviderValue) {
            const provider = WebFontsUtil.fontProvider(fontFamily, FontProvider.fromValue(fontProviderValue));

            // Fall back on a default variant to be compatible with manually entered (non existing) legacy
            // font styles (pre custom fonts)
            const variant = WebFontVariants.fromValue(fontVariant, provider) || WebFontVariants.defaultVariant(provider);
            const css = {
                fontWeight: variant.weight,
                fontStyle: variant.style,
                fontFamily
            };

            switch (provider) {
                case FontProvider.STANDARD:
                    css.cssFamilyNames = StandardWebFontList.cssFamilyNames(fontFamily);
                    break;

                case FontProvider.CUSTOM:
                case FontProvider.GOOGLE:
                case FontProvider.FONTS_DOT_COM:
                    css.cssFamilyNames = fontFamily;
                    break;
                default:
                    break;
            }

            return css;
        },

        /**
         * Returns the Google font family string from the specified `fontDescriptorCollection`, formatted
         * to fit the Google Font API.
         *
         * @param  {FontDescriptorCollection|FontDescriptor[]} fontDescriptorCollection the
         * `FontDescriptorCollection` or array of `FontDescriptor`'s.
         *
         * @retun {String[]} E.g. `["Tangerine:bold,bolditalic", "Inconsolata:italic"`.
         *
         * @see https://github.com/typekit/webfontloader#google
         * @see https://developers.google.com/fonts/docs/getting_started#Syntax
         */
        googleFontFamily(fontDescriptorCollection) {
            if (_.isArray(fontDescriptorCollection)) {
                fontDescriptorCollection = new FontDescriptorCollection(fontDescriptorCollection);
            }

            return _.uniq(fontDescriptorCollection.fromProvider(FontProvider.GOOGLE).map((fontDescriptor) => (
                // Ensure correct casing for Google fonts (there might still be manual leftovers
                // in the db - post "custom fonts")
                `${GoogleFonts.fontFamily(fontDescriptor.get("family"))}:${fontDescriptor.get("variations").toString()}`
            )));
        },

        /**
         * Returns an array of Typekit kit id's contained in the given `fontDescriptorCollection`.
         *
         * @param  {FontDescriptorCollection} fontDescriptorCollection [description]
         * @return {String[]}                 Array of Typekit kit id's.
         */
        getTypekitKitIds(fontDescriptorCollection) {
            return _.uniq(fontDescriptorCollection.fromProvider(FontProvider.TYPEKIT).map((fontDescriptor) => fontDescriptor.get("data").kitID));
        },

        getFontsDotComFamilies(fontDescriptorCollection) {
            if (_.isArray(fontDescriptorCollection)) {
                fontDescriptorCollection = new FontDescriptorCollection(fontDescriptorCollection);
            }

            return _.uniq(fontDescriptorCollection.fromProvider(FontProvider.FONTS_DOT_COM).map((fontDescriptor) => (
                `${fontDescriptor.get("family")}:${fontDescriptor.get("variations").toString()}`
            )));
        },

        getFontsDotComProjectIds(fontDescriptorCollection) {
            return _.uniq(_.map(fontDescriptorCollection.fromProvider(FontProvider.FONTS_DOT_COM), (fontDescriptor) => fontDescriptor.get("data").projectId));
        },

        /**
         * Add quotes around the font family names when it's necessary.
         *
         * Example: "Trebuchet MS, Bauhaus 93, sans-serif" => "Trebuchet MS,'Bauhaus 93',sans-serif".
         *
         * Details: It's a know bug that CKEditor 4 does not quote CSS font family names. This is
         * an issue if the name contains non-identifiers, most often encountered as words that
         * start with a digit, e.g. Avenir 45 Book. Historically we have worked around the issue by
         * filtering out any fonts that contain digits in their name, but this is not compatible
         * with fonts from Fonts.com, since they often contain their weight as part of the name.
         * Note that the ticket discrition on ckeditor.com is wrong about the issue being related
         * all kind of digits in the name of a font family.
         *
         * Does not require escaping: Museo W01_500
         * Requires escaping: Shree Devanagari 714
         *
         * http://dev.ckeditor.com/ticket/12563
         * https://mathiasbynens.be/notes/unquoted-font-family
         */
        quoteSpecialFontNames(fontNames) {
            return fontNames && fontNames
                .split(",")
                .map(this._trim.bind(this))
                .map(this._quoteFontNameIfNecessary.bind(this))
                .join(",");
        },

        /**
         * Given a `FontDescriptor`, returns a list of possible variants.
         *
         * @param  {FontDescriptor} fontDescriptor [description]
         * @return {Object[]}                [description]
         *
         * @see  WebFontVariants
         */
        variantsFromFontDescriptor(fontDescriptor) {
            return WebFontVariants.fromValues(fontDescriptor.get("variations"), fontDescriptor.get("provider"));
        },

        /**
         * From the specified (custom) `FontDescriptor`'s, injects the CSS `font-face` definitions
         * into the `<head>` of the current `document`.
         *
         * `path`, `customFontId` and `format` are required fields of the `FontDescriptor`'s `data` property, i.e. only
         * exposed by Custom Fonts.
         *
         * @param {FontDescriptor[]} fontDescriptors The (custom) `FontDescriptor`'s.
         */
        injectFontFaceCSS(fontDescriptors) {
            const toCSSString = (descriptor) => {
                const {path, format} = descriptor.get("data");
                const url = UrlUtil.getAbsoluteViewerApiUrl(path);

                return `
                    @font-face {
                        font-family: '${descriptor.get("family")}';
                        src: url(${url}) format('${format}');
                    }
                `;
            };

            const cssString = _.reduce(fontDescriptors, (memo, descriptor) => (
                memo + toCSSString(descriptor)
            ), "");

            $(`<style type="text/css">${cssString}</style>`).appendTo("head");
        },

        // StringUtils.trim does not work as expected when used in the map above.
        _trim(fontName) {
            return fontName.trim();
        },

        _quoteFontNameIfNecessary(fontName) {
            if (this._containsNonIdentifier(fontName)) {
                return this._quoteFontName(fontName);
            }

            return fontName;
        },

        _containsNonIdentifier(fontName) {
            const words = fontName.split(/\s+/);
            const allWordsAreIdentifiers = words.every(this._isIdentifier);
            return !allWordsAreIdentifiers;
        },

        _isIdentifier(fontName) {
            // Identifiers may not start with 1) a digit, 2) dash followed by a digit or 3) with
            // two dashes.
            return !(/^(-?\d|--)/.test(fontName));
        },

        _quoteFontName(fontName) {
            return `'${fontName}'`;
        }
    };

    return WebFontsUtil;
});



// WEBPACK FOOTER //
// ./src/shared/ui/experience/fonts/WebFontsUtil.js